import { ChangeDetectionStrategy, Component, computed, input, ViewChild, effect, signal, output } from '@angular/core';
import { GoogleMap, MapAdvancedMarker, MapPolyline, MapDirectionsRenderer } from '@angular/google-maps';

@Component({
  selector: 'app-google-map',
  template: `
    @if (hasContent()) {
      <google-map
        #map
        [height]="height()"
        [width]="width()"
        [center]="mapCenter()"                                    
        
        [zoom]="zoom()"
        [options]="mapOptions"
        (mapInitialized)="onMapInitialized($event)"
      >
        @if (directionsResults()) {
          <map-directions-renderer [directions]="directionsResults()!"></map-directions-renderer>
        } @else {
          @for (marker of markers(); track $index) {
            <map-advanced-marker [position]="marker"></map-advanced-marker>
          }
          @if (markers().length > 1 && showPolyline()) {
            <map-polyline [path]="markers()" [options]="polylineOptions"></map-polyline>
          }
        }
      </google-map>
    } @else {
      <div class="flex align-items-center justify-content-center h-full w-full bg-surface-100 text-500">
        <i class="pi pi-map mr-2"></i> No coordinates or address available
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [GoogleMap, MapAdvancedMarker, MapPolyline, MapDirectionsRenderer],
})
export class GoogleMapComponent {
  @ViewChild('map') mapComponent?: GoogleMap;

  // Inputs
  lat = input<number | undefined>();
  lng = input<number | undefined>();
  locations = input<{ lat?: number, lng?: number }[] | undefined>();
  origin = input<string | undefined>();
  destination = input<string | undefined>();

  height = input<string>('100%');
  width = input<string>('100%');
  zoom = input<number>(12);
  showPolyline = input<boolean>(true);

  // Computed markers
  markers = computed(() => {
    const locs = this.locations();
    if (locs && locs.length > 0) {
      return locs
        .filter(l => l.lat !== undefined && l.lng !== undefined)
        .map(l => ({ lat: l.lat!, lng: l.lng! }));
    }
    if (this.lat() !== undefined && this.lng() !== undefined) {
      return [{ lat: this.lat()!, lng: this.lng()! }];
    }
    return [];
  });

  // Outputs
  routeInfo = output<{ distance: string; duration: string }>();

  // State for address-based route
  directionsResults = signal<google.maps.DirectionsResult | undefined>(undefined);

  hasContent = computed(() => this.markers().length > 0 || this.directionsResults() !== undefined);

  mapCenter = computed(() => {
    const m = this.markers();
    if (m.length === 0) return { lat: 24.7136, lng: 46.6753 };
    if (m.length === 1) return m[0];
    const lat = m.reduce((sum, p) => sum + p.lat, 0) / m.length;
    const lng = m.reduce((sum, p) => sum + p.lng, 0) / m.length;
    return { lat, lng };
  });

  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: false,
    mapId: 'DEMO_MAP_ID',
    gestureHandling: 'cooperative'
  };

  polylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#2563EB',
    strokeOpacity: 0.8,
    strokeWeight: 4,
  };

  private _mapInstance?: google.maps.Map;

  constructor() {
    // Effect to geocode/get directions if no coordinates but names exist
    effect(() => {
      const org = this.origin();
      const dest = this.destination();
      const m = this.markers();

      if (m.length === 0 && org && dest) {
        this.loadDirections(org, dest);
      } else if (!org || !dest) {
        this.directionsResults.set(undefined);
      }
    });

    // Effect to fit bounds when markers change
    effect(() => {
      const m = this.markers();
      if (m.length > 1 && this._mapInstance) {
        setTimeout(() => this.fitBounds(m), 100);
      }
    });
  }

  onMapInitialized(mapInstance: google.maps.Map) {
    this._mapInstance = mapInstance;
    const m = this.markers();
    if (m.length > 1) {
      setTimeout(() => this.fitBounds(m), 100);
    }
  }

  private loadDirections(origin: string, destination: string) {
    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: origin,
        destination: destination,
        travelMode: google.maps.TravelMode.DRIVING
      },
      (response, status) => {
        if (status === 'OK' && response) {
          this.directionsResults.set(response);
          const route = response.routes[0];
          if (route && route.legs && route.legs.length > 0) {
            const leg = route.legs[0];
            this.routeInfo.emit({
              distance: leg.distance?.text || 'N/A',
              duration: leg.duration?.text || 'N/A'
            });
          }
        } else {
          console.warn('Directions request failed due to ' + status);
        }
      }
    );
  }

  private fitBounds(markers: { lat: number, lng: number }[]) {
    if (!this._mapInstance) return;
    const bounds = new google.maps.LatLngBounds();
    markers.forEach(marker => {
      bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
    });
    this._mapInstance.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }
}